/**
 * Documents Vault — admin view.
 *
 * Lists society documents with category + archive filters, supports upload
 * via a multipart dialog, inline download, edit (visibility/title/category),
 * and soft-archive.
 *
 * Backend contract: apps/api/src/modules/documents/routes.ts.
 */
import { useState, useEffect, useMemo, useRef, type DragEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  FileText,
  Plus,
  Archive,
  ArchiveRestore,
  Download,
  Pencil,
  UploadCloud,
  Eye,
  Users,
  ShieldCheck,
  X
} from 'lucide-react';
import { api, getErrorMessage, patch as apiPatch, del as apiDel } from '@/lib/api';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import {
  AdvancedListToolbar,
  useUrlSearchState,
  type FilterChip
} from '@/components/ui/advanced-list-toolbar';
import { CursorPaginator } from '@/components/ui/cursor-paginator';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useCursorAccumulator } from '@/hooks/useCursorAccumulator';
import { formatDateTime } from '@/lib/utils';

type Category =
  | 'bylaws'
  | 'agm_minutes'
  | 'egm_minutes'
  | 'noc_template'
  | 'financial_report'
  | 'budget'
  | 'contract'
  | 'tax_filing'
  | 'insurance'
  | 'vendor_agreement'
  | 'other';

type Visibility = 'all_residents' | 'admins_only' | 'committee_only';

interface SocietyDocument {
  id: string;
  society_id: string;
  uploaded_by_id: string;
  title: string;
  description: string | null;
  category: Category;
  visibility: Visibility;
  storage_key: string;
  storage_kind: string;
  mime_type: string;
  size_bytes: number;
  download_count: number;
  tags: string[];
  effective_from: string | null;
  effective_until: string | null;
  is_archived: boolean;
  created_at: string;
  updated_at: string;
  uploaded_by?: { id: string; full_name: string };
}

interface ListResponse {
  items: SocietyDocument[];
  next_cursor: string | null;
}

const CATEGORY_LABEL: Record<Category, string> = {
  bylaws: 'Bylaws',
  agm_minutes: 'AGM minutes',
  egm_minutes: 'EGM minutes',
  noc_template: 'NOC template',
  financial_report: 'Financial report',
  budget: 'Budget',
  contract: 'Contract',
  tax_filing: 'Tax filing',
  insurance: 'Insurance',
  vendor_agreement: 'Vendor agreement',
  other: 'Other'
};

const VISIBILITY_LABEL: Record<Visibility, string> = {
  all_residents: 'All residents',
  admins_only: 'Admins only',
  committee_only: 'Committee only'
};

const VISIBILITY_VARIANT: Record<Visibility, 'info' | 'alert' | 'muted'> = {
  all_residents: 'info',
  admins_only: 'alert',
  committee_only: 'muted'
};

const CATEGORY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  ...(Object.entries(CATEGORY_LABEL) as Array<[Category, string]>).map(([v, l]) => ({
    value: v,
    label: l
  }))
];

const VISIBILITY_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'All' },
  ...(Object.entries(VISIBILITY_LABEL) as Array<[Visibility, string]>).map(([v, l]) => ({
    value: v,
    label: l
  }))
];

const ACCEPTED_MIME = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'text/plain'
].join(',');

const MAX_FILE_BYTES = 50 * 1024 * 1024;

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function originalFilename(doc: SocietyDocument): string {
  // storage_key looks like "society/<id>/documents/<uuid>-<filename>"
  const tail = doc.storage_key.split('/').pop() ?? doc.title;
  const dash = tail.indexOf('-');
  return dash > 0 ? tail.slice(dash + 1) : tail;
}

// URL-synced filter+sort+page state. Defaults are dropped from the address
// bar so a fresh /documents URL stays clean.
const DOCUMENT_FILTER_DEFAULTS = {
  search: '',
  category: '',
  visibility: '',
  effective_window: 'all', // 'all' | 'on' | 'off'
  archive_status: 'active', // 'active' | 'archived'
  sort: 'created_desc',
  page_size: 50
};

export function DocumentsPage(): JSX.Element {
  const [filterState, setFilterState] = useUrlSearchState(DOCUMENT_FILTER_DEFAULTS);
  const [searchInput, setSearchInput] = useState<string>(filterState.search);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editing, setEditing] = useState<SocietyDocument | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<SocietyDocument | null>(null);
  const queryClient = useQueryClient();

  // Debounce free-text search → URL state to avoid one refetch per keystroke.
  useEffect(() => {
    const t = window.setTimeout(() => {
      if (searchInput !== filterState.search) {
        setFilterState({ search: searchInput });
      }
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const filtersHash = useMemo(
    () =>
      JSON.stringify([
        filterState.search,
        filterState.category,
        filterState.visibility,
        filterState.effective_window,
        filterState.archive_status,
        filterState.sort,
        filterState.page_size
      ]),
    [filterState]
  );

  const queryParams = useMemo(() => {
    const params: Record<string, string | number | boolean> = {
      limit: filterState.page_size,
      sort: filterState.sort,
      archived: filterState.archive_status === 'archived'
    };
    if (filterState.search) params.q = filterState.search;
    if (filterState.category) params.category = filterState.category;
    if (filterState.visibility) params.visibility = filterState.visibility;
    if (filterState.effective_window === 'on') params.effective_window = true;
    if (filterState.effective_window === 'off') params.effective_window = false;
    return params;
  }, [filterState]);

  const [cursor, setCursor] = useState<string | null>(null);
  useEffect(() => {
    setCursor(null);
  }, [filtersHash]);
  const list = useQuery({
    queryKey: ['documents', queryParams, cursor],
    queryFn: () =>
      api
        .get<ListResponse>('/documents', {
          params: { ...queryParams, ...(cursor ? { cursor } : {}) }
        })
        .then((res) => res.data),
    placeholderData: (prev) => prev
  });
  const paginator = useCursorAccumulator<SocietyDocument>(list.data, cursor, filtersHash);

  const docs = paginator.items;

  const showArchived = filterState.archive_status === 'archived';

  const remove = useMutation({
    mutationFn: (id: string) => apiDel<void>(`/documents/${id}`),
    onSuccess: () => {
      toast.success('Document archived');
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const unarchive = useMutation({
    mutationFn: (id: string) => apiPatch<SocietyDocument>(`/documents/${id}`, { is_archived: false }),
    onSuccess: () => {
      toast.success('Document restored');
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
    },
    onError: (e) => toast.error(getErrorMessage(e))
  });

  const downloadDoc = async (doc: SocietyDocument): Promise<void> => {
    try {
      const resp = await api.get(`/documents/${doc.id}/download`, { responseType: 'blob' });
      const blob = resp.data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = originalFilename(doc);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      void queryClient.invalidateQueries({ queryKey: ['documents'] });
    } catch (e) {
      toast.error(getErrorMessage(e));
    }
  };

  // KPIs
  const counts = useMemo(() => {
    const total = docs.length;
    const downloads = docs.reduce((s, d) => s + d.download_count, 0);
    const sizeMb = docs.reduce((s, d) => s + d.size_bytes, 0) / (1024 * 1024);
    return { total, downloads, sizeMb };
  }, [docs]);

  // Sort + filter chip wiring for the shared toolbar.
  const sortOptions = useMemo(
    () => [
      { value: 'created_desc', label: 'Uploaded (newest)' },
      { value: 'created_asc', label: 'Uploaded (oldest)' },
      { value: 'title_asc', label: 'Title (A–Z)' },
      { value: 'effective_from_desc', label: 'Effective from (newest)' }
    ],
    []
  );

  const filterChips: FilterChip[] = useMemo(
    () => [
      {
        id: 'category',
        label: 'Category',
        type: 'select',
        value: filterState.category,
        options: CATEGORY_OPTIONS,
        onChange: (v) => setFilterState({ category: v })
      },
      {
        id: 'visibility',
        label: 'Visibility',
        type: 'select',
        value: filterState.visibility,
        options: VISIBILITY_OPTIONS,
        onChange: (v) => setFilterState({ visibility: v })
      },
      {
        id: 'effective_window',
        label: 'Effective window',
        type: 'toggle',
        value: filterState.effective_window as 'all' | 'on' | 'off',
        onLabel: 'Currently effective',
        offLabel: 'Not currently effective',
        onChange: (v) => setFilterState({ effective_window: v })
      },
      {
        id: 'archive_status',
        label: 'Status',
        type: 'select',
        value: filterState.archive_status,
        options: [
          { value: 'active', label: 'Active' },
          { value: 'archived', label: 'Archived' }
        ],
        onChange: (v) => setFilterState({ archive_status: v })
      }
    ],
    [filterState, setFilterState]
  );

  const hasActiveFilters =
    filterState.search !== DOCUMENT_FILTER_DEFAULTS.search ||
    filterState.category !== DOCUMENT_FILTER_DEFAULTS.category ||
    filterState.visibility !== DOCUMENT_FILTER_DEFAULTS.visibility ||
    filterState.effective_window !== DOCUMENT_FILTER_DEFAULTS.effective_window ||
    filterState.archive_status !== DOCUMENT_FILTER_DEFAULTS.archive_status ||
    filterState.sort !== DOCUMENT_FILTER_DEFAULTS.sort ||
    filterState.page_size !== DOCUMENT_FILTER_DEFAULTS.page_size;

  const resetFilters = () => {
    setFilterState(DOCUMENT_FILTER_DEFAULTS);
    setSearchInput('');
  };

  return (
    <>
      <PageHeader
        title="Documents"
        description="Society-wide official documents — bylaws, AGM/EGM minutes, financials, contracts."
        actions={
          <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
            <DialogTrigger asChild>
              <Button size="sm">
                <Plus className="h-4 w-4" /> Upload document
              </Button>
            </DialogTrigger>
            <UploadDialog
              onClose={() => setUploadOpen(false)}
              onUploaded={() => {
                setUploadOpen(false);
                void queryClient.invalidateQueries({ queryKey: ['documents'] });
              }}
            />
          </Dialog>
        }
      />
      <div className="mx-auto w-full max-w-[1400px] space-y-vt-4 px-vt-6 pb-vt-8 pt-vt-4">
        {/* KPI strip */}
        <div className="grid grid-cols-1 gap-vt-4 md:grid-cols-3">
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-primary-100 text-vt-primary">
                <FileText className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Documents</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {list.isLoading ? <Skeleton className="h-7 w-12" /> : counts.total}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-[color:var(--vt-safe-100)] text-[color:var(--vt-safe)]">
                <Download className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Total downloads</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {list.isLoading ? <Skeleton className="h-7 w-12" /> : counts.downloads}
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-vt-3 p-vt-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-vt-md bg-vt-surface-2 text-vt-text-muted">
                <Archive className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <div>
                <div className="text-vt-12 text-vt-text-muted">Storage used</div>
                <div className="text-vt-24 font-semibold text-vt-text">
                  {list.isLoading ? (
                    <Skeleton className="h-7 w-16" />
                  ) : (
                    `${counts.sizeMb.toFixed(1)} MB`
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Advanced filter toolbar */}
        <AdvancedListToolbar
          searchValue={searchInput}
          onSearchChange={setSearchInput}
          searchPlaceholder="Search by title or description…"
          filters={filterChips}
          sort={{
            value: filterState.sort,
            onChange: (v) => setFilterState({ sort: v }),
            options: sortOptions
          }}
          pageSize={filterState.page_size}
          onPageSizeChange={(n) => setFilterState({ page_size: n })}
          onReset={resetFilters}
          hasActiveFilters={hasActiveFilters}
          resultLabel={
            list.data ? `${docs.length} result${docs.length === 1 ? '' : 's'}` : 'Loading…'
          }
        />

        {/* Table */}
        <Card>
          <CardContent className="p-vt-4">
            {list.isLoading ? (
              <div className="flex flex-col gap-vt-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : list.isError ? (
              <EmptyState
                icon={<FileText className="h-6 w-6" strokeWidth={1.75} />}
                title="Could not load documents"
                description={getErrorMessage(list.error)}
                action={
                  <Button variant="secondary" onClick={() => void list.refetch()}>
                    Try again
                  </Button>
                }
              />
            ) : docs.length === 0 ? (
              hasActiveFilters ? (
                <EmptyState
                  icon={<FileText className="h-6 w-6" strokeWidth={1.75} />}
                  title="No documents match your filters"
                  description="Try widening the filters or clear them to see every document on file."
                  action={
                    <Button variant="secondary" onClick={resetFilters}>
                      Reset filters
                    </Button>
                  }
                />
              ) : (
                <EmptyState
                  icon={<FileText className="h-6 w-6" strokeWidth={1.75} />}
                  title={showArchived ? 'No archived documents' : 'No documents yet'}
                  description={
                    showArchived
                      ? 'Nothing has been archived.'
                      : 'Upload bylaws, AGM minutes, NOC templates and other society documents here.'
                  }
                  action={
                    !showArchived ? (
                      <Button onClick={() => setUploadOpen(true)}>
                        <Plus className="h-4 w-4" /> Upload first document
                      </Button>
                    ) : undefined
                  }
                />
              )
            ) : (
              <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Visibility</TableHead>
                    <TableHead className="text-right">Size</TableHead>
                    <TableHead>Uploaded by</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead className="text-right">Downloads</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {docs.map((d) => (
                    <TableRow key={d.id} className={d.is_archived ? 'opacity-60' : ''}>
                      <TableCell>
                        <div className="flex items-center gap-vt-3">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-vt-sm bg-vt-primary-100 text-vt-primary">
                            <FileText className="h-4 w-4" />
                          </span>
                          <div className="min-w-0">
                            <div className="font-medium text-vt-text">{d.title}</div>
                            {d.description ? (
                              <div className="line-clamp-1 text-vt-12 text-vt-text-muted">{d.description}</div>
                            ) : null}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="muted">{CATEGORY_LABEL[d.category]}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={VISIBILITY_VARIANT[d.visibility]}>
                          {VISIBILITY_LABEL[d.visibility]}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-vt-13 text-vt-text-muted">
                        {formatBytes(d.size_bytes)}
                      </TableCell>
                      <TableCell className="text-vt-text-muted">
                        {d.uploaded_by?.full_name ?? '—'}
                      </TableCell>
                      <TableCell className="text-vt-text-muted">{formatDateTime(d.created_at)}</TableCell>
                      <TableCell className="text-right font-mono text-vt-13 text-vt-text-muted">
                        {d.download_count}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex items-center gap-vt-1">
                          <Button
                            size="sm"
                            variant="secondary"
                            title="Download"
                            onClick={() => void downloadDoc(d)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            title="Edit"
                            onClick={() => setEditing(d)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {d.is_archived ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Restore"
                              disabled={unarchive.isPending}
                              onClick={() => unarchive.mutate(d.id)}
                            >
                              <ArchiveRestore className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="ghost"
                              title="Archive"
                              disabled={remove.isPending}
                              onClick={() => setArchiveTarget(d)}
                            >
                              <Archive className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <CursorPaginator
                hasMore={Boolean(paginator.nextCursor)}
                loading={list.isFetching}
                itemsShown={docs.length}
                pageSize={filterState.page_size}
                onLoadMore={() => {
                  if (paginator.nextCursor) setCursor(paginator.nextCursor);
                }}
              />
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Edit dialog */}
      <Dialog open={editing !== null} onOpenChange={(v) => (v ? null : setEditing(null))}>
        {editing ? (
          <EditDialog
            doc={editing}
            onClose={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              void queryClient.invalidateQueries({ queryKey: ['documents'] });
            }}
          />
        ) : null}
      </Dialog>

      <ConfirmDialog
        open={Boolean(archiveTarget)}
        onOpenChange={(v) => (v ? null : setArchiveTarget(null))}
        title="Archive document"
        description={
          archiveTarget ? (
            <>
              Archive <strong>{archiveTarget.title}</strong>? The file is removed from storage and
              residents can no longer download it. You can restore the metadata row but the
              binary cannot be recovered.
            </>
          ) : null
        }
        confirmLabel="Archive"
        variant="danger"
        loading={remove.isPending}
        onConfirm={() => {
          if (!archiveTarget) return;
          remove.mutate(archiveTarget.id, { onSuccess: () => setArchiveTarget(null) });
        }}
      />
    </>
  );
}

// ─── Upload dialog ────────────────────────────────────────────────────────
function UploadDialog({
  onClose,
  onUploaded
}: {
  onClose: () => void;
  onUploaded: () => void;
}): JSX.Element {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<Category>('bylaws');
  const [visibility, setVisibility] = useState<Visibility>('all_residents');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveUntil, setEffectiveUntil] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const addTag = (raw: string) => {
    const t = raw.trim().slice(0, 30);
    if (!t) return;
    if (tags.includes(t)) return;
    if (tags.length >= 10) {
      toast.error('Up to 10 tags allowed');
      return;
    }
    setTags([...tags, t]);
  };

  const removeTag = (t: string) => setTags(tags.filter((x) => x !== t));

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files[0];
    if (f) acceptFile(f);
  };

  const acceptFile = (f: File) => {
    if (f.size > MAX_FILE_BYTES) {
      toast.error('File exceeds 50 MB cap');
      return;
    }
    setFile(f);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      toast.error('Choose a file');
      return;
    }
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('title', title.trim());
      if (description.trim()) fd.append('description', description.trim());
      fd.append('category', category);
      fd.append('visibility', visibility);
      if (tags.length > 0) fd.append('tags', JSON.stringify(tags));
      if (effectiveFrom) fd.append('effective_from', new Date(`${effectiveFrom}T00:00:00Z`).toISOString());
      if (effectiveUntil) fd.append('effective_until', new Date(`${effectiveUntil}T00:00:00Z`).toISOString());
      fd.append('file', file);
      await api.post('/documents', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Document uploaded');
      onUploaded();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Upload document</DialogTitle>
        <DialogDescription>
          Up to 50 MB. PDF, images, Office docs and text are supported.
        </DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="flex flex-col gap-vt-4">
        <div className="flex flex-col gap-vt-1">
          <Label htmlFor="doc-title">Title</Label>
          <Input
            id="doc-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Bylaws v3 (May 2026)"
            maxLength={200}
            required
          />
        </div>

        <div className="grid grid-cols-2 gap-vt-3">
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="doc-category">Category</Label>
            <Select
              id="doc-category"
              value={category}
              onChange={(e) => setCategory(e.target.value as Category)}
            >
              {Object.entries(CATEGORY_LABEL).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label>Visibility</Label>
            <div className="flex flex-col gap-1 rounded-vt-sm border border-vt-border p-vt-2">
              {(Object.keys(VISIBILITY_LABEL) as Visibility[]).map((v) => (
                <label key={v} className="flex items-center gap-vt-2 text-vt-13 text-vt-text">
                  <input
                    type="radio"
                    name="visibility"
                    value={v}
                    checked={visibility === v}
                    onChange={() => setVisibility(v)}
                    className="accent-vt-primary"
                  />
                  {v === 'all_residents' ? (
                    <Users className="h-3.5 w-3.5 text-vt-text-muted" />
                  ) : v === 'admins_only' ? (
                    <ShieldCheck className="h-3.5 w-3.5 text-vt-text-muted" />
                  ) : (
                    <Eye className="h-3.5 w-3.5 text-vt-text-muted" />
                  )}
                  {VISIBILITY_LABEL[v]}
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-vt-1">
          <Label htmlFor="doc-description">Description (optional)</Label>
          <Textarea
            id="doc-description"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Approved at AGM held on 14 Apr 2026."
            maxLength={500}
          />
        </div>

        <div className="flex flex-col gap-vt-1">
          <Label>Tags</Label>
          <div className="flex flex-wrap items-center gap-1 rounded-vt-sm border border-vt-border p-vt-2">
            {tags.map((t) => (
              <span
                key={t}
                className="inline-flex items-center gap-1 rounded-vt-pill bg-vt-primary-100 px-vt-2 py-0.5 text-vt-12 text-vt-primary"
              >
                {t}
                <button
                  type="button"
                  onClick={() => removeTag(t)}
                  className="text-vt-primary hover:text-vt-alert"
                  aria-label={`Remove tag ${t}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addTag(tagInput);
                  setTagInput('');
                }
              }}
              onBlur={() => {
                if (tagInput.trim()) {
                  addTag(tagInput);
                  setTagInput('');
                }
              }}
              placeholder="Press Enter to add"
              className="flex-1 bg-transparent text-vt-13 outline-none placeholder:text-vt-text-soft"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-vt-3">
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="doc-from">Effective from (optional)</Label>
            <Input
              id="doc-from"
              type="date"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="doc-until">Effective until (optional)</Label>
            <Input
              id="doc-until"
              type="date"
              value={effectiveUntil}
              onChange={(e) => setEffectiveUntil(e.target.value)}
            />
          </div>
        </div>

        {/* File drop zone */}
        <div className="flex flex-col gap-vt-1">
          <Label>File</Label>
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={
              'flex cursor-pointer flex-col items-center justify-center gap-vt-2 rounded-vt-sm border-2 border-dashed p-vt-5 text-center transition-colors ' +
              (dragging
                ? 'border-vt-primary bg-vt-primary-100'
                : 'border-vt-border bg-vt-surface-2 hover:border-vt-primary hover:bg-vt-primary-100')
            }
          >
            <UploadCloud className="h-7 w-7 text-vt-primary" strokeWidth={1.75} />
            {file ? (
              <>
                <div className="font-medium text-vt-text">{file.name}</div>
                <div className="text-vt-12 text-vt-text-muted">
                  {formatBytes(file.size)} · {file.type || 'unknown type'}
                </div>
              </>
            ) : (
              <>
                <div className="text-vt-13 font-medium text-vt-text">
                  Drop file here or click to browse
                </div>
                <div className="text-vt-12 text-vt-text-muted">
                  PDF, images, Office docs, text — up to 50 MB
                </div>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_MIME}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) acceptFile(f);
              }}
              className="hidden"
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting} loadingText="Uploading…" disabled={submitting}>
            Upload
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

// ─── Edit dialog ──────────────────────────────────────────────────────────
function EditDialog({
  doc,
  onClose,
  onSaved
}: {
  doc: SocietyDocument;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const [title, setTitle] = useState(doc.title);
  const [description, setDescription] = useState(doc.description ?? '');
  const [category, setCategory] = useState<Category>(doc.category);
  const [visibility, setVisibility] = useState<Visibility>(doc.visibility);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await apiPatch<SocietyDocument>(`/documents/${doc.id}`, {
        title: title.trim(),
        description: description.trim() ? description.trim() : null,
        category,
        visibility
      });
      toast.success('Document updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Edit document</DialogTitle>
        <DialogDescription>Update metadata. The file itself cannot be replaced — re-upload as a new document.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="flex flex-col gap-vt-4">
        <div className="flex flex-col gap-vt-1">
          <Label htmlFor="edit-title">Title</Label>
          <Input
            id="edit-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            required
          />
        </div>
        <div className="flex flex-col gap-vt-1">
          <Label htmlFor="edit-desc">Description</Label>
          <Textarea
            id="edit-desc"
            rows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
          />
        </div>
        <div className="grid grid-cols-2 gap-vt-3">
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="edit-cat">Category</Label>
            <Select
              id="edit-cat"
              value={category}
              onChange={(e) => setCategory(e.target.value as Category)}
            >
              {Object.entries(CATEGORY_LABEL).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-vt-1">
            <Label htmlFor="edit-vis">Visibility</Label>
            <Select
              id="edit-vis"
              value={visibility}
              onChange={(e) => setVisibility(e.target.value as Visibility)}
            >
              {(Object.keys(VISIBILITY_LABEL) as Visibility[]).map((v) => (
                <option key={v} value={v}>
                  {VISIBILITY_LABEL[v]}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting} loadingText="Saving…" disabled={submitting}>
            Save
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

